import { useEffect, ReactNode, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Header } from './Header'
import { Footer } from './Footer'
import { PortalBottomNav } from './PortalBottomNav'
import { PortalBackground } from './PortalBackground'
import { Alert } from './ui'
import { CloseIcon } from './ui'

/**
 * Draft-translation review banner.
 *
 * Hindi/Marathi translations are best-effort drafts, pending professional medical-translation
 * review. To stay honest, we surface a dismissible notice at the top of <main> whenever the
 * active language is not English. Dismissal is persisted per-language in localStorage so the
 * banner does not nag, but reappears if the user switches to a language they haven't dismissed.
 */
function DraftTranslationNotice({ language }: { language: string }) {
  const { t } = useTranslation()
  const baseLang = (language || 'en').split('-')[0]
  const storageKey = `tb-draft-notice-${baseLang}`

  // Only English needs no banner; nothing is shown there.
  const [dismissed, setDismissed] = useState(
    () => baseLang === 'en' || (typeof window !== 'undefined' && window.localStorage.getItem(storageKey) === '1')
  )

  // Re-evaluate dismissal when the language changes (each language has its own key).
  useEffect(() => {
    if (baseLang === 'en') return
    setDismissed(typeof window !== 'undefined' && window.localStorage.getItem(storageKey) === '1')
  }, [baseLang, storageKey])

  if (baseLang === 'en' || dismissed) return null

  const dismiss = () => {
    if (typeof window !== 'undefined') window.localStorage.setItem(storageKey, '1')
    setDismissed(true)
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
      <div className="relative">
        <Alert tone="info" title={t('common.translationDraftNotice')}>
          {null}
        </Alert>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('common.dismiss', 'Dismiss notice')}
          className="absolute top-2 right-2 p-1.5 rounded-lg text-[rgb(0_96_124)] hover:bg-[rgb(191_237_255)] transition min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          <CloseIcon className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

/**
 * Public portal layout: Header + main landmark + Footer.
 * - Renders the skip-link target (#main-content) so the header skip link works.
 * - Scrolls to top (or to a hash anchor) on route change for predictable nav.
 * - Shows the draft-translation review banner (non-English languages only, dismissible).
 * Used by all public portal pages. Authenticated/social pages keep their own layouts.
 */
export function PortalLayout({ children, mainClassName = '' }: { children: ReactNode; mainClassName?: string }) {
  const { pathname, hash } = useLocation()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1))
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
    }
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  }, [pathname, hash])

  return (
    <div className="min-h-screen flex flex-col relative pb-[60px] xl:pb-0">
      {/* Site-wide ambient background — the login page's blue→white→cyan gradient
          + soft drifting colour orbs, applied across the whole public portal.
          Decorative only: fixed, pointer-events-none, painted behind all content. */}
      <PortalBackground />
      <Header />
      <main id="main-content" tabIndex={-1} className={`relative z-10 flex-1 ${mainClassName}`}>
        <DraftTranslationNotice language={i18n.language} />
        {children}
      </main>
      <Footer />
      {/* Mobile-first thumb-reachable action bar. The 60px bottom padding above
          (mobile only) clears content/footer past the fixed bar + safe-area inset. */}
      <PortalBottomNav />
    </div>
  )
}